import { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Code,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon, ViewIcon } from '@chakra-ui/icons';
import { ArrowRightFromLine } from 'lucide-react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { BillingRulesPreviewModal } from './BillingRulesPreviewModal';
import { Trans } from '@lingui/react/macro';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { Decimal } from 'decimal.js';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import type {
  ProviderInvoiceRuleView,
  ProviderInvoiceRuleFormValues,
} from '@duke-hq/billing/types/rules';
import { RULE_TYPE_SHORT_LABELS } from './constants';
import { formatDate, getRuleParams } from './utils';
import { viewToFormInitial } from '@duke-hq/svc-api/lib/billing/mappers/rules';
import BillingRulesForm from './BillingRulesForm';

function ParamsCell({ rule }: { rule: ProviderInvoiceRuleView }) {
  const params = getRuleParams(rule);

  if (params.length === 0) {
    return <Text>—</Text>;
  }

  return (
    <Text>
      {params.map((param, idx) => (
        <span key={idx}>
          {param.label}: <Badge borderRadius="full">{param.value}</Badge>
          {idx < params.length - 1 && ' '}
        </span>
      ))}
    </Text>
  );
}

export interface BillingRulesListProps {
  providerId: number;
  rules: ProviderInvoiceRuleView[];
  loading?: boolean;
  error?: string | null;
  courses?: Array<{ id: string; title: string; price_aud: Decimal | null }>;
  onCreate: (values: ProviderInvoiceRuleFormValues) => void | Promise<void>;
  onUpdate: (id: string, values: ProviderInvoiceRuleFormValues) => void | Promise<void>;
  onToggleActive: (id: string, active: boolean) => void | Promise<void>;
  onSoftDelete: (id: string) => void | Promise<void>;
}

export function BillingRulesList({
  providerId,
  rules,
  loading,
  error,
  courses,
  onCreate,
  onUpdate,
  onToggleActive,
  onSoftDelete,
}: BillingRulesListProps) {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  const [editing, setEditing] = useState<ProviderInvoiceRuleView | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleting, setDeleting] = useState<ProviderInvoiceRuleView | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();

  const openCreate = () => {
    setEditing(null);
    onOpen();
  };
  const openEdit = (rule: ProviderInvoiceRuleView) => {
    setEditing(rule);
    onOpen();
  };

  const handleSubmitForm = async (values: ProviderInvoiceRuleFormValues) => {
    setIsSaving(true);
    try {
      if (editing) {
        await onUpdate(editing.id, values);
        toast({ status: 'success', title: 'Rule updated' });
      } else {
        await onCreate(values);
        toast({ status: 'success', title: 'Rule created' });
      }
      onClose();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'An unknown error occurred';
      toast({
        status: 'error',
        title: 'Save failed',
        description: message,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading === true) {
    return (
      <Flex align="center" justify="center" p={8}>
        <Spinner />
      </Flex>
    );
  }
  if (error != null && error !== '') {
    return (
      <Box p={4}>
        <Text color="red.500">{error}</Text>
      </Box>
    );
  }

  return (
    <>
      <TableViewLayout
        pageHeader={[
          !isExpanded && (
            <IconButton
              aria-label="expand secondary sidebar"
              icon={<ArrowRightFromLine />}
              variant="tertiary"
              onClick={toggleExpandSubSidebar}
              size="md"
            />
          ),
          <PageHeading key="billing-rules">
            <Trans>Billing Rules</Trans>
          </PageHeading>,
        ]}
        pageActions={[
          <Button
            key="preview-rules"
            variant="ghost"
            leftIcon={<ViewIcon />}
            onClick={onPreviewOpen}
          >
            Preview rules
          </Button>,
          <Button key="create-new-rule" leftIcon={<AddIcon boxSize={3} />} onClick={openCreate}>
            Add new rule
          </Button>,
        ]}
        table={
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Type</Th>
                <Th>Course</Th>
                <Th>Contract type</Th>
                <Th>Contract created between</Th>
                <Th>Priority</Th>
                <Th>Valid</Th>
                <Th>Params</Th>
                <Th>Active</Th>
                <Th w="1%">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rules.map((r) => (
                <Tr key={r.id}>
                  <Td>
                    <Badge borderRadius="full">{RULE_TYPE_SHORT_LABELS[r.type] ?? r.type}</Badge>
                  </Td>
                  <Td>
                    {r.courseId != null && courses != null ? (
                      <Text>{courses.find((c) => c.id === r.courseId)?.title}</Text>
                    ) : (
                      <Text color="gray.500">All</Text>
                    )}
                  </Td>
                  <Td>
                    {r.enrolmentContractType != null ? (
                      <Code>{r.enrolmentContractType}</Code>
                    ) : (
                      <Text color="gray.500">All</Text>
                    )}
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <Badge borderRadius="full">
                        {formatDate(r.enrolmentContractCreatedFrom)}
                      </Badge>
                      <Text>→</Text>
                      <Badge borderRadius="full">{formatDate(r.enrolmentContractCreatedTo)}</Badge>
                    </HStack>
                  </Td>
                  <Td>{r.priority}</Td>
                  <Td>
                    <HStack spacing={1}>
                      <Badge borderRadius="full">{formatDate(r.validFrom)}</Badge>
                      <Text>→</Text>
                      <Badge borderRadius="full">{formatDate(r.validTo)}</Badge>
                    </HStack>
                  </Td>
                  <Td>
                    <ParamsCell rule={r} />
                  </Td>
                  <Td>
                    <HStack>
                      <Switch
                        isChecked={r.active}
                        onChange={(e) => onToggleActive(r.id, e.target.checked)}
                        colorScheme="brand"
                      />
                      <Text fontSize="sm" color={r.active ? 'green.500' : 'gray.500'}>
                        {r.active ? 'Active' : 'Inactive'}
                      </Text>
                    </HStack>
                  </Td>
                  <Td>
                    <IconButton
                      aria-label="Edit"
                      icon={<EditIcon />}
                      size="sm"
                      onClick={() => openEdit(r)}
                    />
                    <IconButton
                      aria-label="Delete"
                      icon={<DeleteIcon />}
                      size="sm"
                      variant="ghost"
                      ml={2}
                      onClick={() => setDeleting(r)}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        }
      />
      <Drawer isOpen={isOpen} onClose={onClose} size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader>
            <Text key={'billing-rules'} textStyle="xl" fontWeight="medium">
              {editing ? 'Edit Rule' : 'New Rule'}
            </Text>
          </DrawerHeader>
          <DrawerBody>
            <BillingRulesForm
              providerId={providerId}
              initial={
                editing
                  ? viewToFormInitial(editing)
                  : { providerId, type: 'PRICE_OVERRIDE', active: true, priority: 100 }
              }
              courses={courses}
              onSubmit={handleSubmitForm}
              onCancel={onClose}
              isSaving={isSaving}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <BillingRulesPreviewModal isOpen={isPreviewOpen} onClose={onPreviewClose} rules={rules} />

      <ConfirmationDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        title={<Trans>Delete rule?</Trans>}
        body={
          <Trans>
            This will mark the rule as deleted so it won't be applied to new billable items.
          </Trans>
        }
        onConfirm={async () => {
          if (!deleting) {
            return;
          }
          try {
            setIsDeleting(true);
            await onSoftDelete(deleting.id);
            setDeleting(null);
          } finally {
            setIsDeleting(false);
          }
        }}
        confirmLabel={<Trans>Delete</Trans>}
        variant="destructive"
        isLoading={isDeleting}
      />
    </>
  );
}
